import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { adsApi, categoriesApi, fileUploadApi, promotionPackagesApi, authApi, paymentApi, CreateAdRequest } from '../services/api';
import toast from 'react-hot-toast';
import TimchehLoader from '../components/TimchehLoader';

interface AdForm {
  title: string;
  description: string;
  price: number;
  categoryId: string;
  subCategoryId?: string;
  city: string;
  province: string;
  postalCode: string;
  address?: string;
  images?: FileList;
  [key: string]: any; // Allow dynamic attribute fields
}

// Geocode address using Geoapify Autocomplete API (returns first match lat/lng)
// Uses the Geoapify autocomplete endpoint as requested. If the API call fails or returns no result,
// falls back to a default coordinate (Toronto).
const geocodeAddress = async (city: string, province: string, postalCode: string): Promise<{ lat: number; lng: number }> => {
  const defaultCoords = { lat: 43.6532, lng: -79.3832 }; // Toronto fallback

  try {
    const apiKey = '4029fbc311864b87b98cb258e033c1a9'; // Provided API key
    const text = [city, province, postalCode].filter(Boolean).join(' ');
    if (!text) return defaultCoords;

    const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(text)}&apiKey=${apiKey}`;

    const resp = await fetch(url);
    if (!resp.ok) {     
      return defaultCoords;
    }

    const data = await resp.json();

    // Geoapify returns GeoJSON-like features. Try several locations for lat/lng.
    const feature = data?.features?.[0];
    if (feature) {
      // geometry.coordinates is [lon, lat]
      if (feature.geometry && Array.isArray(feature.geometry.coordinates)) {
        const [lon, lat] = feature.geometry.coordinates;
        if (typeof lat === 'number' && typeof lon === 'number') {
          return { lat, lng: lon };
        }
      }

      // properties.lat / properties.lon (some responses include these)
      if (feature.properties && typeof feature.properties.lat === 'number' && typeof feature.properties.lon === 'number') {
        return { lat: feature.properties.lat, lng: feature.properties.lon };
      }
    }

    return defaultCoords;
  } catch (error) {  
    return { lat: 43.6532, lng: -79.3832 };
  }
};

const CreateAdPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categoryError, setCategoryError] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [selectedSubSubCategory, setSelectedSubSubCategory] = useState<string>('');
  const [categoryHierarchy, setCategoryHierarchy] = useState<string[]>([]);
  const [selectedPromotionPackage, setSelectedPromotionPackage] = useState<string>('');
  const [selectedPromotionPackageData, setSelectedPromotionPackageData] = useState<any>(null);
  const [pendingPayment, setPendingPayment] = useState<boolean>(false);
  const [marketingCode, setMarketingCode] = useState<string>('');
  const [uploadedImages, setUploadedImages] = useState<any[]>([]);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [aiImageIndex, setAiImageIndex] = useState<number | null>(null);
  const [aiGenerating, setAiGenerating] = useState<boolean>(false);
  const [createdAdId, setCreatedAdId] = useState<string>('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [blockReason, setBlockReason] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<AdForm>();

  // Address autocomplete state (Geoapify)
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [isLoadingAddressSuggestions, setIsLoadingAddressSuggestions] = useState(false);
  const addressDebounceRef = useRef<number | null>(null);

  const fetchAddressSuggestions = async (text: string) => {
    try {
      if (!text || text.trim().length < 3) {
        setAddressSuggestions([]);
        setIsLoadingAddressSuggestions(false);
        return;
      }
      setIsLoadingAddressSuggestions(true);
      const apiKey = '4029fbc311864b87b98cb258e033c1a9';
      const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(text)}&limit=5&apiKey=${apiKey}`;
      const resp = await fetch(url);
      if (!resp.ok) {
        setAddressSuggestions([]);
        setIsLoadingAddressSuggestions(false);
        return;
      }
      const data = await resp.json();
      const features = data?.features || [];

      const suggestions = features.map((f: any) => {
        const props = f.properties || {};
        return {
          formatted: props.formatted || props.name || f?.properties?.address || '',
          city: props.city || props.county || props.state || '',
          state: props.state || '',
          postcode: props.postcode || props.postalcode || props.postal_code || '',
          lat: f?.geometry?.coordinates?.[1],
          lon: f?.geometry?.coordinates?.[0]
        };
      });

      setAddressSuggestions(suggestions.filter((s: any) => s.formatted));
      setShowAddressSuggestions(true);
      setIsLoadingAddressSuggestions(false);
    } catch (err) {      
      setAddressSuggestions([]);
      setIsLoadingAddressSuggestions(false);
    }
  };

  const handleAddressInputChange = (value: string) => {
    setAddressQuery(value);
    if (addressDebounceRef.current) {
      window.clearTimeout(addressDebounceRef.current);
    }
    // Show loading indicator when user starts typing (after debounce delay)
    if (value && value.trim().length >= 3) {
      setIsLoadingAddressSuggestions(true);
    } else {
      setIsLoadingAddressSuggestions(false);
    }
    // debounce
    addressDebounceRef.current = window.setTimeout(() => {
      fetchAddressSuggestions(value);
    }, 300) as unknown as number;
  };

  const selectAddressSuggestion = (s: any) => {
    // Fill form fields with selected suggestion
    setAddressQuery(s.formatted || '');
    setValue('address', s.formatted || '');
    if (s.city) setValue('city', s.city);
    if (s.state) setValue('province', s.state);
    if (s.postcode) setValue('postalCode', s.postcode);
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
  };

  // NOTE: Do not pre-register fields that appear only on later steps (address/postalCode).
  // They will be registered inline on the inputs when their step is mounted so they are
  // not validated prematurely. This helps per-step validation semantics.

  const translatePackageName = (name: string) => {
    const translations: { [key: string]: string } = {
      'Gold Package': 'پلن طلایی',
      'Premium Package': 'پلن ویژه',
      'Free Package': 'پلن رایگان',
      'Gold Plan': 'پلن طلایی',
      'Premium Plan': 'پلن ویژه',
      'Free Plan': 'پلن رایگان'
    };
    return translations[name.trim()] || name;
  };

  const translatePackageDescription = (description: string) => {
    const translations: { [key: string]: string } = {
      'Maximum visibility with all features': 'حداکثر نمایش با تمام ویژگی‌ها',
      'Featured ad with advanced features': 'آگهی ویژه با ویژگی‌های پیشرفته',
      'Standard ad with basic features': 'آگهی استاندارد با ویژگی‌های پایه'
    };
    return translations[description.trim()] || description;
  };

  const translateFeature = (feature: string) => {
    let trimmedFeature = feature.trim();
    const hasCheckmark = trimmedFeature.startsWith('✓');
    if (hasCheckmark) {
      trimmedFeature = trimmedFeature.substring(1).trim();
    }
    
    // Handle dynamic features like "30-day Display" or "day Display-30"
    if (trimmedFeature.includes('day Display') || trimmedFeature.match(/\d+-?day Display/i)) {
      const dayMatch = trimmedFeature.match(/(\d+)/);
      if (dayMatch) {
        const days = dayMatch[1];
        return `${hasCheckmark ? '✓ ' : ''}نمایش ${days} روزه`;
      }
    }
    
    const translations: { [key: string]: string } = {
      'Standard Ad': 'آگهی استاندارد',
      'Standard ad display': 'نمایش آگهی استاندارد',
      'Standard Ad Display': 'نمایش آگهی استاندارد',
      'Standard Search Position': 'موقعیت استاندارد در جستجو',
      'Featured Badge': 'نشان ویژه',
      'Highlight Ad': 'برجسته کردن آگهی',
      'Urgent Badge': 'نشان فوری',
      'Bump Up Ad': 'ارتقای آگهی',
      'Homepage Gallery Display': 'نمایش در گالری صفحه اصلی',
      '2x More Views': '۲ برابر بازدید بیشتر',
      '5x More Views': '۵ برابر بازدید بیشتر'
    };
    
    // Check for exact match first
    if (translations[trimmedFeature]) {
      return `${hasCheckmark ? '✓ ' : ''}${translations[trimmedFeature]}`;
    }
    
    // Check for partial matches (for features with counts like "Featured Badge (2 items)")
    for (const [english, persian] of Object.entries(translations)) {
      if (trimmedFeature.startsWith(english)) {
        let result = trimmedFeature.replace(english, persian);
        result = result.replace(/\((\d+)\s*items?\)/gi, '($1 مورد)');
        return `${hasCheckmark ? '✓ ' : ''}${result}`;
      }
    }
    
    // If already in Persian or no translation found, return as is with checkmark
    return hasCheckmark ? `✓ ${trimmedFeature}` : trimmedFeature;
  };

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.getAll()
  });

  const { data: categoryAttributes } = useQuery({
    queryKey: ['category-attributes', selectedSubSubCategory || selectedSubCategory || selectedCategory],
    queryFn: () => categoriesApi.getAttributes(selectedSubSubCategory || selectedSubCategory || selectedCategory),
    enabled: !!(selectedSubSubCategory || selectedSubCategory || selectedCategory)
  });

  const { data: userPlans } = useQuery({
    queryKey: ['user-plans'],
    queryFn: async () => {
      const response = await paymentApi.getUserPlans();
              // User plans loaded successfully
      return response;
    }
  });

  // If the user has no active plans with remaining ads, redirect them to the Plans page
  // This enforces purchasing/selecting a plan before posting an ad.
  useEffect(() => {
    if (!userPlans) return; // wait until the query resolves

    try {
      const plans = Array.isArray(userPlans.data) ? userPlans.data : [];
      const hasAvailablePlan = plans.some((plan: any) => plan.isActive && !plan.isExpired && plan.hasRemainingAds);
      if (!hasAvailablePlan) {
        // Set a blocking reason so the UI can show an explanatory message
        setBlockReason('شما پلن فعالی با آگهی باقی‌مانده ندارید. برای ثبت آگهی باید پلن خریداری یا فعال کنید.');
      } else {
        // Clear blocking reason when user has an available plan
        setBlockReason(null);
      }
    } catch (err) {
      // If something unexpected happens, do nothing here and allow normal flow
    
    }
  }, [userPlans]);

  const { data: promotionPackages } = useQuery({
    queryKey: ['promotion-packages'],
    queryFn: () => promotionPackagesApi.getActive(),
    enabled: !userPlans?.data || userPlans.data.length === 0 || !userPlans.data.some(plan => plan.isActive && !plan.isExpired && plan.hasRemainingAds)
  });

  // Mutation for activating free plans (called when user selects a free package)
  const activateFreePlanMutation = useMutation({
    mutationFn: async (packageId: string) => {
      const response = await paymentApi.activateFreePlan(packageId);
      return response.data;
    },
    onSuccess: async (data) => {
      toast.success('پلن رایگان با موفقیت فعال شد!');
      // Refresh user plans so UI reflects newly activated plan
      await queryClient.invalidateQueries({ queryKey: ['user-plans'] });
      // Refetch to ensure we have the latest data
      await queryClient.refetchQueries({ queryKey: ['user-plans'] });

      // If backend returns a planId, use it to set the selected promotion package
      if (data?.planId) {
        setSelectedPromotionPackage(data.planId);
        const pkg = promotionPackages?.data?.find((p: any) => p.id === data.planId);
        if (pkg) setSelectedPromotionPackageData(pkg);
      }
    },
    onError: () => {
      toast.error('خطا در فعال‌سازی پلن رایگان');
    }
  });

  // Handle payment return
  useEffect(() => {
    const paymentStatus = searchParams.get('paymentStatus');
    const sessionId = searchParams.get('sessionId');
    
    if (paymentStatus) {
      if (paymentStatus === 'success' && sessionId) {
        // Verify payment through backend
        const verifyPayment = async () => {
          try {
            toast.loading('در حال تأیید پرداخت...');
            
            const response = await paymentApi.verifyPayment(sessionId);
            
            toast.dismiss();
            
            if (response.data.status === 'success' && response.data.payment) {
              const payment = response.data.payment;
              const packageId = payment.promotionPackageId;
              
              if (packageId) {
                setSelectedPromotionPackage(packageId);
                setPendingPayment(false);
                
                // Find the package data
                const pkg = promotionPackages?.data?.find(p => p.id === packageId);
                if (pkg) {
                  setSelectedPromotionPackageData(pkg);
                  toast.success(`پرداخت موفق! پکیج "${pkg.name}" فعال شد.`);
                }
              }
              
              // Set step to 3 to show the confirmation
              setStep(3);
            } else {
              toast.error('تأیید پرداخت ناموفق بود.');
              setStep(3);
            }
          } catch (error: any) {
            toast.dismiss();
            toast.error(error.response?.data?.message || 'خطا در تأیید پرداخت');
            setStep(3);
          }
        };
        
        verifyPayment();
      } else if (paymentStatus === 'cancelled') {
        toast('پرداخت لغو شد.');
        setStep(3);
      } else if (paymentStatus === 'failed') {
        toast.error('پرداخت ناموفق بود. لطفاً دوباره تلاش کنید.');
        setStep(3);
      }
      
      // Clean up URL parameters
      navigate('/post-ad', { replace: true });
    }
  }, [searchParams, promotionPackages, navigate]);

  const createAdMutation = useMutation({
    mutationFn: (data: CreateAdRequest) => adsApi.create(data),
    onSuccess: async (response) => {
      const adId = response.data?.id;
      const adSlug = response.data?.slug;
      
      // Invalidate user plans query to refresh the data after ad creation
      queryClient.invalidateQueries({ queryKey: ['user-plans'] });
      
      if (!adId) {
        toast.error('خطا در دریافت شناسه آگهی. لطفاً دوباره تلاش کنید.');
        return;
      }
      
      setCreatedAdId(adId);
      
      // Navigate directly to the ad page if no images to upload
      if (selectedImages.length === 0) {
        setIsPublishing(false);
        toast.dismiss(); // Dismiss any loading toasts
                  toast.success('آگهی شما با موفقیت منتشر شد!');
        // Use slug-based URL if available, fallback to ID
        const adUrl = adSlug ? `/ads/${adSlug}` : `/ad/${adId}`;
        navigate(adUrl);
        return;
      }

      // Upload selected images after ad creation
      if (selectedImages.length > 0) {   
        
        // Check authentication status before uploading
        try {
          const authResponse = await authApi.checkAuth();
          // Authentication status checked - if we get here, user is authenticated
        } catch (error) {
          // If checkAuth fails, user is not authenticated
          toast.error('برای آپلود تصاویر باید به حساب کاربری خود وارد شوید. لطفاً وارد شوید و دوباره تلاش کنید.');
          setStep(4);
          return;
        }

    
        
        // Add longer delay to ensure ad is fully committed to database
        await new Promise(resolve => setTimeout(resolve, 3000));
        
                  toast('📤 در حال آپلود تصاویر...', { 
          duration: 8000,
          style: { direction: 'rtl' }
        });
        try {
          // Process uploads sequentially to avoid overwhelming the server
          for (let i = 0; i < selectedImages.length; i++) {
            const file = selectedImages[i];
            const isPrimary = i === 0; // First image is primary   
            
            try {
              // Check if file is image or video and use appropriate endpoint
              if (file.type.startsWith('image/')) {
                            // Uploading image file
                const result = await fileUploadApi.uploadImage(adId, file, isPrimary);
              
              } else if (file.type.startsWith('video/')) {
                // Uploading video file
                const result = await fileUploadApi.uploadVideo(adId, file);
             
              } else {
             
                toast.error(`Unsupported file type: ${file.name}`);
                continue;
              }
              
              // Add small delay between uploads to avoid server overload
              if (i < selectedImages.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 500));
              }
            } catch (uploadError: any) {
             
              
              // Show specific error message from API (including Persian messages)
              const errorMessage = uploadError.response?.data?.message || `Error uploading ${file.name}`;
              toast.error(errorMessage, {
                duration: 6000,
                style: { direction: 'ltr', fontFamily: 'Arial, sans-serif' }
              });
              
              // Continue with other files instead of stopping all uploads
              continue;
            }
          }
          toast.dismiss(); // Dismiss any loading toasts
          setIsPublishing(false);
                      toast.success('آگهی شما با موفقیت منتشر شد و تصاویر آپلود شدند!');
                  } catch (error: any) {           
            toast.dismiss(); // Dismiss any loading toasts
          
          // Don't show errors that might confuse users - just let them know they can add images later
          toast.dismiss(); // Dismiss any processing toasts
          setIsPublishing(false);
          toast('آگهی با موفقیت منتشر شد! می‌توانید بعداً از صفحه ویرایش تصاویر را اضافه کنید.', {
            duration: 5000,
            icon: '⚠️'
          });
        }
      }
      
      // Navigate directly to the ad page after successful creation and image upload
      // Use slug-based URL if available, fallback to ID
      const adUrl = adSlug ? `/ads/${adSlug}` : `/ad/${adId}`;
      navigate(adUrl);
    },
    onError: (error: any) => {
      setIsPublishing(false);
      toast.dismiss(); // Dismiss any loading toasts
      toast.error(error.response?.data?.message || 'ایجاد آگهی ناموفق بود.');
    }
  });

  const onSubmit = async (data: AdForm) => {
    // onSubmit entry (debug log removed)
    if (step < 4) {
      // Validate step 1: Category selection
      if (step === 1) {
              if (!selectedCategory) {
        // show inline error in case toasts are not visible
        setCategoryError('لطفاً یک دسته‌بندی انتخاب کنید');
        // also keep toast for visibility when it works
        try { toast.error('لطفاً یک دسته‌بندی انتخاب کنید'); } catch {}
        return;
        }
      }
      
      // Validate step 2: Title and description
      if (step === 2) {
              if (!data.title || !data.description) {
        toast.error('Please fill all required fields (title, description)');
        return;
        }
      }
      
      setStep(step + 1);
      return;
    }

    if (step === 4) {
      // Set publishing state immediately when user clicks انتشار آگهی
      setIsPublishing(true);
      // Validate all required fields before submission
      if (!selectedCategory) {
        setIsPublishing(false);
        toast.error('لطفاً یک دسته‌بندی انتخاب کنید');
        setStep(1);
        return;
      }

      if (!data.title || !data.description) {
        setIsPublishing(false);
        toast.error('Please fill all required fields (title, description)');
        setStep(2);
        return;
      }

      if (!data.city || !data.province || !data.postalCode || !data.price) {
        setIsPublishing(false);
        toast.error('لطفاً فیلدهای الزامی (شهر، استان، کد پستی، قیمت) را پر کنید');
        return;
      }

      if (!selectedPromotionPackage) {     
        setIsPublishing(false);
        toast.error('لطفاً یک پلن تبلیغاتی انتخاب کنید');
        setStep(3);
        return;
      }

      if (pendingPayment) {
        setIsPublishing(false);
        toast.error('لطفاً ابتدا پرداخت پکیج انتخاب شده را تکمیل کنید');
        setStep(3);
        return;
      }

      try {
        // Get coordinates for the location
        const coordinates = await geocodeAddress(data.city, data.province, data.postalCode);
        
        const adData: CreateAdRequest = {
          title: data.title.trim(),
          description: data.description.trim(),
          price: Number(data.price),
          categoryId: selectedSubSubCategory || selectedSubCategory || selectedCategory,
          subCategoryId: undefined, // Use only categoryId for the deepest selection
          city: data.city.trim(),
          province: data.province,
          postalCode: data.postalCode.trim(),
          address: data.address?.trim() || `${data.city.trim()}, ${data.province}`,
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          attributes: categoryAttributes?.data?.reduce((acc: any, attr) => {
            const value = (data as any)[`attr_${attr.id}`];
            if (value) {
              acc[attr.id] = value;
            }
            return acc;
          }, {}) || {},
          promotionPackageId: selectedPromotionPackage,
          marketingCode: marketingCode.trim() || undefined
        };
      
        // Show loading message for the entire process (without blocking overlay)
        toast('⏳ در حال ایجاد آگهی...', { 
          duration: 6000,
          style: { direction: 'rtl' }
        });
        
        createAdMutation.mutate(adData);
      } catch (error) {
          setIsPublishing(false);
        toast.error('خطا در پردازش مکان. لطفاً دوباره تلاش کنید.');
      }
    }
  };

  const handleFileUpload = async (files: FileList) => {
    const fileArray = Array.from(files);
    const maxFiles = 5;
    
    // Check if total files would exceed limit
    if (selectedImages.length + fileArray.length > maxFiles) {
      toast.error(`شما فقط می‌توانید حداکثر ${maxFiles} فایل آپلود کنید`);
      return;
    }
    
    // Validate file types, sizes, and filenames
    const validFiles: File[] = [];
    const maxSize = 10 * 1024 * 1024; // 10MB for videos, 5MB for images
    
    for (const file of fileArray) {
      // Check if file is image or video
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        toast.error(`${file.name} یک تصویر یا ویدیوی معتبر نیست`);
        continue;
      }
      
      // Validate filename characters (same validation as backend)
      const allowedCharsPattern = /^[a-zA-Z0-9\-_\.\s]+$/;
      if (!allowedCharsPattern.test(file.name)) {
        toast.error(`نام فایل "${file.name}" شامل کاراکترهای نامعتبر است. لطفاً فقط از حروف انگلیسی، اعداد، خط تیره (-) و زیرخط (_) استفاده کنید.`, {
          duration: 6000,
          style: { direction: 'rtl', fontFamily: 'iranSans, sans-serif' }
        });
        continue;
      }
      
      // Different size limits for images vs videos
      const sizeLimit = file.type.startsWith('video/') ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > sizeLimit) {
        const limitMB = file.type.startsWith('video/') ? '10MB' : '5MB';
        toast.error(`${file.name} خیلی بزرگ است. حداکثر اندازه ${limitMB} مگابایت است`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length === 0) return;
    
    // Create previews for valid files
    const newPreviews: string[] = [];
    for (const file of validFiles) {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target?.result as string);
        // Update state when all previews are loaded
        if (newPreviews.length === validFiles.length) {
          setSelectedImages(prev => [...prev, ...validFiles]);
          setImagePreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    // If the removed image was the one used for AI, clear the selection
    setAiImageIndex(prev => (prev === index ? null : (prev && prev > index ? prev - 1 : prev)));
  };

  const handleGenerateFromImage = async (index: number) => {
    // Only one image can be used for AI generation
    const file = selectedImages[index];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('لطفاً یک فایل تصویری برای تولید هوش مصنوعی انتخاب کنید');
      return;
    }

    try {
      setAiGenerating(true);
      toast.loading('در حال تولید عنوان و توضیحات از تصویر...');
      const resp = await fileUploadApi.uploadImageTemp(file);
      toast.dismiss();
      const aiTitle = resp.data?.title;
      const aiDescription = resp.data?.description;
      if (aiTitle) {
        // Only prefill if user hasn't typed a title yet
        const currentTitle = (watch('title') || '').trim();
        if (!currentTitle) setValue('title', aiTitle);
      }
      if (aiDescription) {
        const currentDesc = (watch('description') || '').trim();
        if (!currentDesc) setValue('description', aiDescription);
      }
      setAiImageIndex(index);
      toast.success('پیشنهادات هوش مصنوعی اعمال شد (می‌توانید آن‌ها را ویرایش کنید)');
    } catch (error: any) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || 'خطا در تولید متادیتای هوش مصنوعی');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleCategoryChange = (categoryId: string) => {    
    setSelectedCategory(categoryId);
    setSelectedSubCategory('');
    setSelectedSubSubCategory('');
    setCategoryHierarchy([categoryId]);
    setValue('categoryId', categoryId);
    if (categoryId) setCategoryError('');
  };

  const handleSubCategoryChange = (subCategoryId: string) => {
    setSelectedSubCategory(subCategoryId);
    setSelectedSubSubCategory('');
    setCategoryHierarchy([selectedCategory, subCategoryId]);
  };

  const handleSubSubCategoryChange = (subSubCategoryId: string) => {
    setSelectedSubSubCategory(subSubCategoryId);
    setCategoryHierarchy([selectedCategory, selectedSubCategory, subSubCategoryId]);
  };

  const handlePromotionPackageSelect = async (pkg: any) => {
    // Ensure only one plan can be selected at a time
    if (selectedPromotionPackage === pkg.id) {
      // If clicking the same package, deselect it
      setSelectedPromotionPackage('');
      setSelectedPromotionPackageData(null);
      setPendingPayment(false);
      return;
    }

    setSelectedPromotionPackage(pkg.id);
    setSelectedPromotionPackageData(pkg);

    // Check if it's a paid plan
    if (pkg.price > 0) {
      // For paid plans, we need to handle payment
      setPendingPayment(true);
      toast(`پکیج ${pkg.name} انتخاب شد. پرداخت $${pkg.price.toFixed(2)} برای تکمیل الزامی است.`);
      
      // Create secure payment session through backend
      try {
        toast('💳 در حال ایجاد جلسه پرداخت...', { 
          duration: 5000,
          style: { direction: 'rtl' }
        });
        
        const response = await paymentApi.createSession({
          packageId: pkg.id,
          packageName: pkg.name,
          amount: pkg.price
        });
        
        toast.dismiss();
                  toast.success('در حال هدایت به درگاه پرداخت...');
        
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkoutUrl;
        
      } catch (error: any) {
        toast.dismiss();
                  toast.error(error.response?.data?.message || 'خطا در ایجاد جلسه پرداخت');
        setSelectedPromotionPackage('');
        setSelectedPromotionPackageData(null);
        setPendingPayment(false);
      }
    } else {
      // Free plan, no payment needed
      setPendingPayment(false);
              toast.success(`پکیج رایگان "${pkg.name}" انتخاب شد.`);
      // Activate free plan on the backend (same behavior as PlansPage)
      try {
        toast.loading('در حال فعال‌سازی پلن رایگان...');
        activateFreePlanMutation.mutate(pkg.id);
      } catch (err) {
        // mutation handles errors via onError
      }
    }
  };

  // Get parent categories (those without parentCategoryId)
  const parentCategories = categories?.data?.filter(cat => !cat.parentCategoryId) || [];
  
  // Get subcategories for selected parent category
  const availableSubCategories = categories?.data?.filter(cat => cat.parentCategoryId === selectedCategory) || [];
  
  // Get sub-subcategories for selected subcategory
  const availableSubSubCategories = categories?.data?.filter(cat => cat.parentCategoryId === selectedSubCategory) || [];

  if (blockReason) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-start justify-center" dir="rtl">
        <div className="w-full max-w-xl px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10 text-center border border-gray-100">
            <div className="mx-auto flex items-center justify-center w-20 h-20 rounded-full bg-red-50 mb-4">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <defs>
                  <linearGradient id="g1" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor="#EF4444" />
                    <stop offset="100%" stopColor="#DC2626" />
                  </linearGradient>
                </defs>
                <path d="M12 9v4" stroke="url(#g1)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 17h.01" stroke="url(#g1)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10.29 3.86 2.88 18.14a1.5 1.5 0 0 0 1.29 2.26h15.66a1.5 1.5 0 0 0 1.29-2.26L13.71 3.86a1.5 1.5 0 0 0-2.42 0z" stroke="url(#g1)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="rgba(239,68,68,0.06)" />
              </svg>
            </div>

            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">امکان ثبت آگهی وجود ندارد</h2>
            <p className="text-gray-600 mb-6 text-lg">{blockReason}</p>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => navigate('/plans')}
                className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md focus:outline-none focus:ring-2 focus:ring-red-200"
              >
                <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M12 5v14M5 12h14" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                مشاهده پلن‌ها
              </button>

              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center px-6 py-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors focus:outline-none"
              >
                بازگشت به صفحه اصلی
              </button>
            </div>

            <p className="text-xs text-gray-400 mt-6">اگر برای انتخاب پلن نیاز به کمک دارید، به صفحه راهنما مراجعه کنید یا با پشتیبانی ما تماس بگیرید.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* timchehLoader with dim overlay during submission */}
      {(isSubmitting || isPublishing) && (
        <TimchehLoader 
          fullScreen={true}
          size="large" 
          message="در حال انتشار آگهی..." 
        />
      )}
      
      <div className="min-h-screen bg-gray-50 py-8" dir="rtl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Progress Bar */}
          <div className="bg-gray-50 px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-semibold text-gray-900">ثبت آگهی</h1>
              <div className="flex items-center space-x-4">
                {[1, 2, 3, 4].map((stepNumber) => (
                  <div
                    key={stepNumber}
                    className={`flex items-center ${stepNumber < 4 ? 'mr-4' : ''}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        step >= stepNumber
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {stepNumber}
                    </div>
                    {stepNumber < 4 && (
                      <div
                        className={`w-16 h-1 ${
                          step > stepNumber ? 'bg-primary-600' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              {step === 1 && 'انتخاب دسته‌بندی و افزودن تصاویر'}
              {step === 2 && 'توضیحات محصول'}
              {step === 3 && 'انتخاب پلن تبلیغاتی'}
              {step === 4 && 'تعیین قیمت و مکان'}
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6">
            {/* Step 1: Category and Photos */}
            {step === 1 && (
              <div className="space-y-6">
                {/* Cascading Category Selection */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      دسته‌بندی *
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white bg-no-repeat bg-left-4 bg-[length:12px] pr-3 pl-10"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'left 0.75rem center'
                      }}
                    >
                                              <option value="">انتخاب دسته‌بندی</option>
                      {parentCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {categoryError && (
                      <p className="mt-2 text-sm text-red-600">{categoryError}</p>
                    )}
                  </div>

                  {availableSubCategories.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        زیردسته‌بندی
                      </label>
                      <select
                        value={selectedSubCategory}
                        onChange={(e) => handleSubCategoryChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white bg-no-repeat bg-left-4 bg-[length:12px] pr-3 pl-10"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'left 0.75rem center'
                        }}
                      >
                        <option value="">انتخاب زیردسته‌بندی (اختیاری)</option>
                        {availableSubCategories.map((subCategory) => (
                          <option key={subCategory.id} value={subCategory.id}>
                            {subCategory.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {availableSubSubCategories.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        زیر-زیردسته‌بندی
                      </label>
                      <select
                        value={selectedSubSubCategory}
                        onChange={(e) => handleSubSubCategoryChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white bg-no-repeat bg-left-4 bg-[length:12px] pr-3 pl-10"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'left 0.75rem center'
                        }}
                      >
                        <option value="">انتخاب زیر-زیردسته‌بندی (اختیاری)</option>
                        {availableSubSubCategories.map((subSubCategory) => (
                          <option key={subSubCategory.id} value={subSubCategory.id}>
                            {subSubCategory.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Display selected category path */}
                  {selectedCategory && (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                                              <p className="text-sm text-gray-600 mb-1">دسته‌بندی انتخاب شده:</p>
                      <div className="flex items-center text-sm font-medium text-primary-600">
                                               {categoryHierarchy.map((catId, index) => {
                         const category = categories?.data?.find(c => c.id === catId);
                         
                         return (
                           <span key={catId} className="flex items-center">
                             {category?.name}
                             {index < categoryHierarchy.length - 1 && (
                               <svg className="w-4 h-4 mx-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                               </svg>
                             )}
                           </span>
                         );
                       })}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    تصاویر
                  </label>
                  
                  {/* Image Previews */}
                  {imagePreviews.length > 0 && (
                    <div className="mb-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={preview}
                              alt={`پیش‌نمایش ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                            {/* <div className="absolute left-2 top-2">
                              {aiImageIndex === index ? (
                                <span className="inline-flex items-center px-2 py-1 bg-primary-600 text-white text-xs rounded">AI</span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleGenerateFromImage(index)}
                                  disabled={aiGenerating}
                                  className="inline-flex items-center px-2 py-1 bg-white text-xs rounded border border-gray-200 hover:bg-gray-50"
                                >
                                  {aiGenerating && aiImageIndex === index ? (
                                    <svg className="animate-spin h-4 w-4 mr-1" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                                    </svg>
                                  ) : null}
                                  Use for AI
                                </button>
                              )}
                            </div> */}
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        {selectedImages.length} از ۵ تصویر انتخاب شده
                      </p>
                    </div>
                  )}
                  
                  {/* Upload Area */}
                  {selectedImages.length < 5 && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-4">
                        <label htmlFor="images" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            {selectedImages.length === 0 ? 'آپلود حداکثر ۵ فایل (تصویر و ویدیو)' : `افزودن فایل‌های بیشتر (${5 - selectedImages.length} باقی مانده)`}
                          </span>
                          <span className="mt-1 block text-sm text-gray-500">
                                                          تصاویر: PNG، JPG، GIF تا ۵ مگابایت | ویدیو: MP4، MOV تا ۱۰ مگابایت
                          </span>
                          <input
                            id="images"
                            type="file"
                            multiple
                            accept="image/*,video/*"
                            className="sr-only"
                            onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                  
                  {selectedImages.length >= 5 && (
                    <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
                                                <p className="text-sm text-green-700">حداکثر تعداد تصاویر انتخاب شده (۵/۵)</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Title and Description */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    عنوان *
                  </label>
                  <input
                    {...register('title', { required: 'عنوان الزامی است', maxLength: { value: 100, message: 'عنوان باید حداکثر ۱۰۰ کاراکتر باشد' } })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="چه چیزی می‌فروشید؟"
                    maxLength={100}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.title ? (
                      <p className="text-sm text-red-600">{errors.title.message}</p>
                    ) : (
                      <div />
                    )}
                    <div className="text-xs text-gray-500">
                      {(watch('title') || '').length}/100
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    توضیحات *
                  </label>
                  <textarea
                    {...register('description', { required: 'توضیحات الزامی است', maxLength: { value: 2000, message: 'توضیحات باید حداکثر ۲۰۰۰ کاراکتر باشد' } })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="محصول خود را به تفصیل توصیف کنید..."
                    maxLength={2000}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.description ? (
                      <p className="text-sm text-red-600">{errors.description.message}</p>
                    ) : (
                      <div />
                    )}
                    <div className="text-xs text-gray-500">
                      {(watch('description') || '').length}/2000
                    </div>
                  </div>
                </div>

                {/* Category-specific attributes */}
                {categoryAttributes?.data?.map((attr) => (
                  <div key={attr.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {attr.label} {attr.isRequired && '*'}
                    </label>
                    {attr.type === 'Text' && (
                      <input
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false
                        })}
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={attr.defaultValue || ''}
                      />
                    )}
                    {attr.type === 'Number' && (
                      <input
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false
                        })}
                        type="number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={attr.defaultValue || ''}
                      />
                    )}
                    {attr.type === 'TextArea' && (
                      <textarea
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false
                        })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={attr.defaultValue || ''}
                      />
                    )}
                    {attr.type === 'Dropdown' && attr.options && (
                      <select
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white bg-no-repeat bg-right-4 bg-[length:12px] pl-3 pr-10"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.75rem center'
                        }}
                      >
                        <option value="">انتخاب {attr.label}</option>
                        {(() => {
                          try {
                            return JSON.parse(attr.options).map((option: string) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ));
                          } catch {
                            return attr.options.split('\n').filter(opt => opt.trim()).map((option: string) => (
                              <option key={option.trim()} value={option.trim()}>
                                {option.trim()}
                              </option>
                            ));
                          }
                        })()}
                      </select>
                    )}
                    {attr.type === 'Boolean' && (
                      <div className="flex items-center space-x-3">
                        <input
                          {...register(`attr_${attr.id}` as any)}
                          type="checkbox"
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-600">{attr.label}</span>
                      </div>
                    )}
                    {attr.type === 'Date' && (
                      <input
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false
                        })}
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    )}
                    {attr.type === 'MultiSelect' && attr.options && (
                      <div className="space-y-2">
                        {(() => {
                          try {
                            return JSON.parse(attr.options).map((option: string) => (
                              <div key={option} className="flex items-center space-x-3">
                                <input
                                  {...register(`attr_${attr.id}` as any)}
                                  type="checkbox"
                                  value={option}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">{option}</span>
                              </div>
                            ));
                          } catch {
                            return attr.options.split('\n').filter(opt => opt.trim()).map((option: string) => (
                              <div key={option.trim()} className="flex items-center space-x-3">
                                <input
                                  {...register(`attr_${attr.id}` as any)}
                                  type="checkbox"
                                  value={option.trim()}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">{option.trim()}</span>
                              </div>
                            ));
                          }
                        })()}
                      </div>
                    )}
                {attr.type === 'Url' && (
                      <input
                         {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false,
                          pattern: {
                              value: /^https?:\/\/.+/,
                              message: 'لطفاً آدرس URL معتبر وارد کنید'
                 }
                })}
                        type="url"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="https://example.com"
                      />
 )}

            {attr.type === 'Email' && (
  <input
    {...register(`attr_${attr.id}` as any, {
      required: attr.isRequired ? `${attr.label} is required` : false,
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: 'لطفاً آدرس ایمیل معتبر وارد کنید'
      }
    })}
    type="email"
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
    placeholder="example@email.com"
  />
)}

            {attr.type === 'Date' && (
  <input
    {...register(`attr_${attr.id}` as any, {
      required: attr.isRequired ? `${attr.label} is required` : false
    })}
    type="date"
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
  />
)}

            {attr.type === 'Email' && (
  <input
    {...register(`attr_${attr.id}` as any, {
      required: attr.isRequired ? `${attr.label} is required` : false,
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: 'لطفاً آدرس ایمیل معتبر وارد کنید'
      }
    })}
    type="email"
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
    placeholder={attr.defaultValue || ''}
  />
)}

      {attr.type === 'Url' && (
  <input
    {...register(`attr_${attr.id}` as any, {
      required: attr.isRequired ? `${attr.label} is required` : false,
      pattern: {
        value: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
        message: 'لطفاً آدرس وب‌سایت معتبر وارد کنید'
      }
    })}
    type="url"
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
    placeholder={attr.defaultValue || 'https://'}
  />
)}

                    {errors[`attr_${attr.id}`] && (
                      <p className="mt-1 text-sm text-red-600">
                        {(errors[`attr_${attr.id}`] as any)?.message}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Step 3: Promotion Package Selection */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">یکی از پلن‌های موجود خود را انتخاب کنید</h2>
                  <p className="text-gray-600">
                                          {userPlans?.data && userPlans.data.some(plan => plan.isActive && !plan.isExpired && plan.hasRemainingAds)
                        ? 'یکی از پلن‌های موجود خود را انتخاب کنید:'
                        : 'لطفاً یک پلن برای آگهی خود انتخاب کنید. (الزامی)'
                    }
                  </p>
                </div>

                {/* Show User's Own Plans if Available */}
                {userPlans?.data && userPlans.data.some(plan => plan.isActive && !plan.isExpired && plan.hasRemainingAds) ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">پلن‌های شما:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {userPlans.data
                        .filter(plan => plan.isActive && !plan.isExpired && plan.hasRemainingAds)
                        .map((plan) => (
                          <div
                            key={plan.id}
                            className={`border rounded-lg p-6 hover:border-${plan.colorClass}-300 transition-colors cursor-pointer relative ${
                              selectedPromotionPackage === plan.promotionPackageId 
                                ? `border-2 border-${plan.colorClass}-500 bg-${plan.colorClass}-50`
                                : 'border-gray-200'
                            }`}
                            onClick={() => {
                              const packageId = plan.promotionPackageId || plan.id;                            
                              setSelectedPromotionPackage(packageId);
                              setSelectedPromotionPackageData(plan);
                              setPendingPayment(false);
                            }}
                          >
                            {selectedPromotionPackage === plan.promotionPackageId && (
                              <div className="absolute -top-3 right-4">
                                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                                  انتخاب شده
                                </span>
                              </div>
                            )}
                     <div className="text-center">
  <div className={`w-12 h-12 bg-${plan.colorClass}-100 rounded-full flex items-center justify-center mx-auto mb-4`}>
    <span className="text-2xl">{plan.iconEmoji}</span>
  </div>
  <h3 className="text-lg font-semibold text-gray-900 mb-2">{translatePackageName(plan.name)}</h3>
  <div className="text-sm text-green-600 font-medium mb-2">پلن خریداری شده</div>
  <div className="text-sm text-gray-600 mb-4">
    باقی‌مانده: {plan.remainingAdsCount} از {plan.maxAdsCount} آگهی
  </div>
  <div className="text-sm text-gray-500 mb-4">
    انقضا: {new Date(plan.expirationDate).toLocaleDateString('fa-IR')}
  </div>
  <ul className="text-sm text-gray-600 space-y-2 text-right">
    {plan.features.split(',').map((feature, index) => (
      <li key={index} className="flex items-center gap-2">
        <span className="text-green-500">✓</span>
        {translateFeature(feature)}
      </li>
    ))}
  </ul>
</div>

                          </div>
                        ))}
                    </div>
                    
                    {/* Option to buy new plans */}
                 <div className="mt-8 p-4 bg-gray-50 rounded-lg">
  <p className="text-sm text-gray-600 mb-3">به پلن جدید نیاز دارید؟</p>
  <button
    type="button"
    onClick={() => navigate('/plans')}
    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
  >
    ← مشاهده و خرید پلن‌های جدید
  </button>
</div>

                  </div>
                ) : promotionPackages?.data && promotionPackages.data.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {promotionPackages.data.map((pkg) => (
                      <div
                        key={pkg.id}
                        className={`border rounded-lg p-6 transition-colors cursor-pointer relative ${
                          selectedPromotionPackage === pkg.id
                            ? pendingPayment && pkg.price > 0
                              ? `border-2 border-orange-500 bg-orange-50`
                              : `border-2 border-${pkg.colorClass}-500 bg-${pkg.colorClass}-50`
                            : 'border-gray-200'
                        }`}
                        onClick={() => handlePromotionPackageSelect(pkg)}
                      >
                        {selectedPromotionPackage === pkg.id && (
                          <div className="absolute -top-3 right-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              pendingPayment && pkg.price > 0
                                ? 'bg-orange-500 text-white'
                                : 'bg-green-500 text-white'
                            }`}>
                              {pendingPayment && pkg.price > 0 ? 'در انتظار پرداخت' : 'انتخاب شده'}
                            </span>
                          </div>
                        )}
                        {pkg.isPopular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
  <span className={`bg-${pkg.colorClass}-500 text-white px-3 py-1 rounded-full text-sm font-medium`}>
    محبوب‌ترین
  </span>
</div>

                        )}
                        <div className="text-center">
                          <div className={`w-12 h-12 bg-${pkg.colorClass}-100 rounded-full flex items-center justify-center mx-auto mb-4`}>
                            <span className="text-2xl">{pkg.iconEmoji}</span>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{translatePackageName(pkg.name)}</h3>
                          <div className="text-3xl font-semibold text-gray-900 mb-1">
                            {pkg.price === 0 ? 'رایگان' : `$${pkg.price.toFixed(2)}`}
                          </div>
                          {pkg.price > 0 && (
                            <div className="text-sm text-gray-500 mb-4">
                             برای {pkg.durationDays} روز
                            </div>
                          )}
                          <ul className="text-sm text-gray-600 space-y-2 mb-6 text-right">
                            {pkg.features.map((feature, index) => (
                              <li key={index}>{translateFeature(feature)}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">در حال بارگذاری پکیج‌های تبلیغاتی...</p>
                  </div>
                )}

                {/* Marketing Code Field (Optional) */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <label htmlFor="marketingCode" className="block text-sm font-medium text-gray-700 mb-2">
                    کد بازاریابی (اختیاری)
                  </label>
                  <input
                    type="text"
                    id="marketingCode"
                    value={marketingCode}
                    onChange={(e) => setMarketingCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="اگر کد بازاریابی دارید، وارد کنید"
                    maxLength={50}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    اگر کد بازاریابی از یک بازاریاب دارید، آن را اینجا وارد کنید
                  </p>
                </div>
              </div>
            )}

            {/* Step 4: Price and Location */}
            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                    قیمت (CAD) *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      defaultValue={0}
                      {...register('price', {
                           
                          min: { value: 0, message: 'قیمت باید بیشتر از ۰ باشد' }
                      })}
                      type="number"
                      step="0.01"
                      dir="ltr"
                      className="w-full pr-7 pl-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left"
                      placeholder="0.00"
                    />
                  </div>
                  {errors.price && (
                    <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                    آدرس
                  </label>
                  <div className="relative">
                    <input
                      {...register('address')}
                      type="text"
                      dir="ltr"
                      onChange={(e) => {
                        // keep react-hook-form in sync and trigger autocomplete
                        setValue('address', (e.target as HTMLInputElement).value);
                        handleAddressInputChange(e.target.value);
                      }}
                      onBlur={(e) => {
                        // ensure suggestions close
                        setTimeout(() => setShowAddressSuggestions(false), 150);
                      }}
                      onFocus={() => { if (addressSuggestions.length > 0) setShowAddressSuggestions(true); }}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left"
                      placeholder="123 Main Street (اختیاری - در صورت عدم ارائه، شهر/استان استفاده می‌شود)"
                      defaultValue={addressQuery}
                    />
                    {isLoadingAddressSuggestions && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}

                    {showAddressSuggestions && addressSuggestions.length > 0 && (
                      <ul className="absolute z-40 left-0 right-0 bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-60 overflow-auto text-left">
                        {addressSuggestions.map((s, idx) => (
                          <li
                            key={idx}
                            onMouseDown={() => selectAddressSuggestion(s)}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          >
                            <div className="text-sm text-gray-900">{s.formatted}</div>
                            <div className="text-xs text-gray-500">{[s.city, s.state, s.postcode].filter(Boolean).join(' • ')}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                      شهر *
                    </label>
                    <input
                                              {...register('city', { required: 'شهر الزامی است' })}
                      type="text"
                      dir="ltr"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left"
                      placeholder="Toronto"
                    />
                    {errors.city && (
                      <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-2">
                      استان *
                    </label>
                    <select
                                              {...register('province', { required: 'استان الزامی است' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white bg-no-repeat bg-left-4 bg-[length:12px] pr-3 pl-10"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'left 0.75rem center'
                      }}
                    >
                                              <option value="">انتخاب استان</option>
                      <option value="ON">Ontario</option>
                      <option value="QC">Quebec</option>
                      <option value="BC">British Columbia</option>
                      <option value="AB">Alberta</option>
                      <option value="MB">Manitoba</option>
                      <option value="SK">Saskatchewan</option>
                      <option value="NS">Nova Scotia</option>
                      <option value="NB">New Brunswick</option>
                      <option value="NL">Newfoundland and Labrador</option>
                      <option value="PE">Prince Edward Island</option>
                      <option value="NT">Northwest Territories</option>
                      <option value="NU">Nunavut</option>
                      <option value="YT">Yukon</option>
                    </select>
                    {errors.province && (
                      <p className="mt-1 text-sm text-red-600">{errors.province.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-2">
                    کد پستی *
                  </label>
                  <input
                    {...register('postalCode', {
                      required: 'کد پستی الزامی است',
                      pattern: {
                        value: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
                        message: 'لطفاً یک کد پستی معتبر وارد کنید (مثال: M5V 3L9)'
                      }
                    })}
                    type="text"
                    dir="ltr"
                    onBlur={(e) => {
                      // normalize postal code: uppercase and insert space after 3 characters
                      const val = (e.target as HTMLInputElement).value || '';
                      let normalized = val.toUpperCase().trim();
                      // remove extra spaces
                      normalized = normalized.replace(/\s+/g, '');
                      if (normalized.length >= 6) {
                        normalized = normalized.slice(0,3) + ' ' + normalized.slice(3,6);
                      }
                      setValue('postalCode', normalized);
                    }}
                    onChange={(e) => {
                      // keep form state updated while typing
                      setValue('postalCode', (e.target as HTMLInputElement).value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left"
                    placeholder="M5V 3L9"
                  />
                  {errors.postalCode && (
                    <p className="mt-1 text-sm text-red-600">{errors.postalCode.message}</p>
                  )}
                </div>
              </div>
            )}



            {/* Navigation Buttons */}
            {step <= 4 && (
              <div className="flex items-center justify-between pt-8 mt-8 border-t border-gray-200">
                {/* Previous Button Container */}
                <div className="flex">
                  {step > 1 && (
                    <button
                      type="button"
                      onClick={() => setStep(step - 1)}
                      className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors font-medium"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                      قبلی
                    </button>
                  )}
                </div>

                {/* Next Button Container */}
                <div className="flex">
                  <button
                    type="button"
                    onClick={() => {
                      // Ensure the react-hook-form submit handler runs even if native submit is blocked
                      handleSubmit(onSubmit)();
                    }}
                    disabled={isSubmitting || (step === 3 && pendingPayment)}
                    className="inline-flex items-center px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                        در حال ارسال...
                      </>
                    ) : (
                      <>
                        {step === 3 && pendingPayment ? 'در انتظار تکمیل پرداخت...' : step < 4 ? 'بعدی' : 'انتشار آگهی'}
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default CreateAdPage; 
